'use client';

import { useEffect, useRef } from 'react';

const WaveGridBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === 'undefined') return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width: number, height: number;
    let mouseX = -9999;
    let mouseY = -9999;

    const resizeCanvas = () => {
      width = canvas.width = window.innerWidth * window.devicePixelRatio;
      height = canvas.height = window.innerHeight * window.devicePixelRatio;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform before scaling
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    window.addEventListener('mousemove', handleMouseMove);

    const drawWaveGrid = () => {
      ctx.clearRect(0, 0, width, height);

      const cols = 60;
      const rows = 30;
      const colSpacing = width / window.devicePixelRatio / cols;
      const rowSpacing = height / window.devicePixelRatio / rows;
      const time = Date.now() / 600;
      const baseOpacity = 0.2;

      for (let i = 0; i < cols; i++) {
        const x = i * colSpacing + colSpacing / 2;

        for (let j = 0; j < rows; j++) {
          const y = j * rowSpacing + rowSpacing / 2;
          const offset = (i + j) * 0.3;
          const pulse = Math.sin(time + offset) * 15;

          const dx = mouseX - x;
          const dy = mouseY - y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const interaction = Math.max(0, 1 - distance / 200);
          const finalHeight = pulse + interaction * 50;
          const opacity = baseOpacity + interaction * (1.0 - baseOpacity);

          ctx.beginPath();
          ctx.strokeStyle = `rgba(3, 191, 158, ${opacity})`;
          ctx.lineWidth = 1;
          ctx.moveTo(x, y - finalHeight / 2);
          ctx.lineTo(x, y + finalHeight / 2);
          ctx.stroke();
        }
      }

      requestAnimationFrame(drawWaveGrid);
    };

    drawWaveGrid();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="wave-canvas"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        display: 'block',
        background: 'linear-gradient(to bottom, #0E1110, #0F423A)',
      }}
    />
  );
};

export default WaveGridBackground;
